import { z } from 'zod';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1PlatformSchema } from './vocabulary';
/**
 * Public AI-instruction inventory contract for `/api/v1` (KIR-4431, slice 1).
 *
 * Field names follow `apps/backend/src/routes/v1/NAMING.md`.
 *
 * **This resource is gated more narrowly than the rest of Inventory** —
 * manager, owner or support, copied from the UI route. An AI instruction
 * describes what a coding agent is configured to do, attributed to named
 * developers, and the app does not show it to a plain member.
 *
 * **The file bodies are not published.** The internal API exposes a `/content`
 * sub-resource returning the raw instruction text; `/api/v1` deliberately does
 * not, per the decision recorded on KIR-4431. Instruction files are
 * user-authored and can contain anything, so publishing them is a decision to
 * take explicitly rather than by proximity.
 */
/** One version of an instruction file's classification. */
export const v1AiInstructionTypeSchema = z
    .string()
    .describe('How Kirin classified this instruction file — for example `rule`, `skill` or `command`. Null when it could not be classified. Deliberately an open string rather than a named enum: the classifier can emit a new category without an API change, so freezing the vocabulary here would make a detection improvement a breaking change.');
/**
 * One AI instruction in the organization's inventory.
 *
 * A row is one **file content in one classification**, aggregated across every
 * developer and project that has it. The same content classified two ways is two
 * rows, which is why `id` encodes both and is opaque.
 */
export const v1AiInstructionSchema = z
    .object({
    id: z
        .string()
        .describe('Opaque identifier for this instruction within your organization. Stable, and safe to store and pass back to the by-ID, users and projects endpoints. **Do not parse or construct it** — it encodes more than one component, because the same file content classified two ways is two rows, and its format is not part of this contract.'),
    name: z
        .string()
        .nullable()
        .describe('The name Kirin generated for this instruction, or null when none was generated.'),
    summary: z
        .string()
        .nullable()
        .describe('A short generated description of what the instruction tells the agent to do, or null when none was generated.'),
    type: v1AiInstructionTypeSchema.nullable(),
    typeName: z
        .string()
        .nullable()
        .describe('That classification as the Kirin app labels it, e.g. "Configuration" for `configuration`. Null whenever `type` is. Published alongside the key because the screen shows the label and a raw category leaves a customer building a report with nothing to render.'),
    filePath: z
        .string()
        .describe('The representative path for this instruction — the one the Inventory screen shows.'),
    filePaths: z
        .array(z.string())
        .describe('Every distinct path this instruction content was found at, across your endpoint users. A superset of `filePath`.'),
    platform: v1PlatformSchema
        .nullable()
        .describe('Where this instruction applies, with the display name the Kirin app shows. Markdown instruction files (`.md`, `.mdc`) and files under `.agents/skills` report `multi-tool` ("Multi-IDE / Platform"), whichever IDE or coding agent found them, because several tools read them. Other files report the IDE or coding agent they were found in. Null when it could not be attributed to one.'),
    quarantined: z
        .boolean()
        .describe('Whether Kirin quarantined this instruction file. Quarantining empties the file on disk, so without this a quarantined row is indistinguishable from a genuinely empty one.'),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this instruction. The endpoint users themselves are on the `users` sub-resource.'),
    projectCount: z
        .number()
        .int()
        .describe('How many projects this instruction was found in. The projects themselves are on the `projects` sub-resource.'),
    firstSeenAt: z
        .string()
        .datetime()
        .describe('When this instruction was first reported, ISO 8601 UTC.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report of this instruction, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1AiInstruction',
    title: 'AI instruction',
    description: "One AI-instruction file in your organization's inventory, in one classification, aggregated across every endpoint user and project that has it.",
    examples: [
        {
            id: 'WyI3ZDJmMWI4ZS00YTA2LTRjOTMtYjVlMS0wOGZhNjJkMzc0YzUiLCJydWxlIl0',
            name: 'CLAUDE.md',
            summary: 'Repository conventions: commit format, test layout, and review rules.',
            type: 'rule',
            typeName: 'Rule',
            filePath: 'CLAUDE.md',
            filePaths: ['CLAUDE.md', 'docs/CLAUDE.md'],
            platform: { id: 'multi-tool', name: 'Multi-IDE / Platform' },
            quarantined: false,
            userCount: 19,
            projectCount: 7,
            firstSeenAt: '2026-06-02T08:41:19Z',
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** Sortable fields, named for the public field they sort. */
export const v1AiInstructionSortFieldSchema = z
    .enum(['name', 'type', 'filePath', 'summary', 'projectCount', 'userCount'])
    .describe('Which field to sort by.');
/**
 * The list query.
 *
 * The filters the Inventory → AI Instructions screen offers, per the ticket:
 * name, type and file.
 *
 * Deliberately unnamed (no `.meta({ id })`): a named schema is emitted as a
 * `$ref`, and `@fastify/swagger` cannot expand a `$ref` into a parameter list.
 */
export const v1AiInstructionListQuerySchema = v1PaginationQuerySchema.extend({
    name: csvParam('Filter by the generated instruction name.'),
    type: csvParam('Filter by classification, as returned in `type` — such as `rule` or `skill`. Filter on the key, not the `typeName` label.'),
    filePath: csvParam('Filter by file path.'),
    sortBy: v1AiInstructionSortFieldSchema.default('userCount'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1AiInstructionListResponseSchema = z
    .object({
    data: z.array(v1AiInstructionSchema).describe('This page of instructions.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1AiInstructionList',
    title: 'AI instruction list',
    description: "A page of the AI-instruction files found across your organization's endpoint users.",
    examples: [
        {
            data: [
                {
                    id: 'WyI3ZDJmMWI4ZS00YTA2LTRjOTMtYjVlMS0wOGZhNjJkMzc0YzUiLCJydWxlIl0',
                    name: 'CLAUDE.md',
                    summary: 'Repository conventions: commit format, test layout, and review rules.',
                    type: 'rule',
                    typeName: 'Rule',
                    filePath: 'CLAUDE.md',
                    filePaths: ['CLAUDE.md', 'docs/CLAUDE.md'],
                    platform: { id: 'multi-tool', name: 'Multi-IDE / Platform' },
                    quarantined: false,
                    userCount: 19,
                    projectCount: 7,
                    firstSeenAt: '2026-06-02T08:41:19Z',
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
            pagination: {
                page: 1,
                pageSize: 25,
                totalItems: 58,
                totalPages: 3,
                hasMore: true,
            },
        },
    ],
});
/** One developer who has a given instruction. */
export const v1AiInstructionUserSchema = z
    .object({
    id: z
        .string()
        .describe('The endpoint user, as identified in the inventory-users resource. Join on this to read their full inventory record.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
    workspaceFolders: z
        .array(z.string())
        .describe('The workspace folders in which this endpoint user has the instruction.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe("This endpoint user's most recent report, ISO 8601 UTC."),
})
    .meta({
    id: 'V1AiInstructionUser',
    title: 'AI instruction user',
    description: 'An endpoint user who has a given AI instruction.',
    examples: [
        {
            id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
            name: 'Stella Normal',
            email: 'stella@example.com',
            workspaceFolders: ['~/src/payments-api', '~/src/billing-web'],
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** The users of one instruction. Not paginated. */
export const v1AiInstructionUserListResponseSchema = z
    .object({
    data: z
        .array(v1AiInstructionUserSchema)
        .describe('Every endpoint user who has this instruction.'),
})
    .meta({
    id: 'V1AiInstructionUserList',
    title: 'AI instruction users',
    description: 'Every endpoint user in your organization who has a given AI instruction. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: '8c47a0e5-1b93-4d26-b7f8-5e0a2c96d341',
                    name: 'Stella Normal',
                    email: 'stella@example.com',
                    workspaceFolders: ['~/src/payments-api', '~/src/billing-web'],
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
        },
    ],
});
/** One project a given instruction was found in. */
export const v1AiInstructionProjectSchema = z
    .object({
    id: z.string().describe("The project's identifier."),
    name: z.string().describe("The project's name."),
    slug: z.string().describe("The project's slug, as used in URLs."),
    userCount: z
        .number()
        .int()
        .describe('How many of your endpoint users have this instruction in this project.'),
    lastSeenAt: z
        .string()
        .datetime()
        .describe('The most recent report of this instruction in this project, ISO 8601 UTC.'),
})
    .meta({
    id: 'V1AiInstructionProject',
    title: 'AI instruction project',
    description: 'A project a given AI instruction was found in.',
    examples: [
        {
            id: 'a4d1e70b-8c25-4936-9f0e-3b6a1d82c5f7',
            name: 'payments-api',
            slug: 'payments-api',
            userCount: 11,
            lastSeenAt: '2026-08-31T14:03:07Z',
        },
    ],
});
/** The projects of one instruction. Not paginated. */
export const v1AiInstructionProjectListResponseSchema = z
    .object({
    data: z
        .array(v1AiInstructionProjectSchema)
        .describe('Every project this instruction was found in.'),
})
    .meta({
    id: 'V1AiInstructionProjectList',
    title: 'AI instruction projects',
    description: 'Every project in your organization where a given AI instruction was found. Not paginated.',
    examples: [
        {
            data: [
                {
                    id: 'a4d1e70b-8c25-4936-9f0e-3b6a1d82c5f7',
                    name: 'payments-api',
                    slug: 'payments-api',
                    userCount: 11,
                    lastSeenAt: '2026-08-31T14:03:07Z',
                },
            ],
        },
    ],
});
//# sourceMappingURL=ai-instructions.js.map